import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { MultiSelect } from 'app/shared/select';
import { useResourceQuery } from 'hooks/automation/resources';
import React, { useEffect, useState } from 'react';
import styles from './response.module.css';
import { useEffectsService } from 'hooks/automation/effects';
import { Checkbox } from 'app/shared/input/checkbox';
import { NeuronDelayConfig } from './delay';
import { SimpleInput } from 'app/shared/input';

export const NeuronResponseConfig = ({ data = {}, onAdd = () => {}, closeModal = () => {} }) => {
  const { fetchEffects } = useEffectsService();
  const { fetchResources } = useResourceQuery();

  const [attr_key, setAttrKey] = useState('');
  const [attr_value, setAttrValue] = useState('');
  const [effects, setEffects] = useState([]);
  const [resources, setResources] = useState([]);
  const [rule, setRule] = useState(null);
  const [selected_action, setSelectedAction] = useState(null);
  const [selected_resources, setSelectedResources] = useState([]);
  const [show_delay_config, setShowDelayConfig] = useState(false);

  useEffect(() => {
    fetchEffects().then(({ effects: raw_effects }) => {
      const parsed_effects = raw_effects.map((effect) => ({
        ...effect,
        label: effect.name,
        value: effect.code
      }));

      setEffects(() => parsed_effects);
      if (data && data.effect) {
        setSelectedAction(() => effects.find((effect) => effect.value === data.effect));
      }
    });

    if (data && data.rule) {
      setShowDelayConfig(() => true);
      setRule(data.rule);
    }

    return () => clearForm();
  }, [data]);

  useEffect(() => {
    setSelectedResources([]);
    if (!selected_action || !Object.keys(selected_action).length) {
      return;
    }

    if (selected_action.resource_class === 'attribute') {
      if (data && data.target) {
        const [, config] = data.target.split(':');
        const attr = config.split(',');
        setAttrKey(attr[0]);
        setAttrValue(attr[1]);
      }

      return;
    }

    fetchResources(selected_action.resource_class, '').then(({ resources: raw_resources }) => {
      const parsed_resources = raw_resources.map((res) => ({
        ...res,
        label:
          selected_action.resource_class === 'tag' ? res.value : res.name || res.title || res.link,
        value: selected_action.resource_class === 'tag' ? res.code : res.id
      }));

      setResources(() => parsed_resources);
      if (data && data.target) {
        const [, ...resource_ids] = data.target.split(':');
        const res_ids =
          selected_action.resource_class === 'tag' ? resource_ids[0].split(',') : resource_ids;
        const id_maps = res_ids?.reduce((s, r) => ({ ...s, [r]: r }), {});
        setSelectedResources(parsed_resources.filter((resource) => id_maps[resource.value]));
      }
    });
  }, [selected_action]);

  useEffect(() => {
    if (!show_delay_config) setRule(null);
  }, [show_delay_config]);

  const clearForm = () => {
    setSelectedAction({});
    setSelectedResources([]);
  };

  const handleActionSelection = (selection = null) => {
    if (!selection) return;
    setSelectedAction(selection);
  };

  const handleResourceSelection = (selections = []) => {
    setSelectedResources(selections);
  };

  const is_forn_valid = () => {
    return selected_action?.resource_class === 'attribute'
      ? attr_key && attr_value
      : selected_resources?.length > 0;
  };

  const submit = () => {
    const resources = selected_resources?.map((res) => res.value) || 'none';
    let separator = ':';
    if (selected_action.resource_class === 'tag') {
      separator = ',';
    }

    let target = `${selected_action.resource_class}:${resources.join(separator)}`;
    if (selected_action.resource_class === 'attribute') {
      target = `${selected_action.resource_class}:${attr_key},${attr_value}`;
    }

    const new_data = {
      effect: selected_action.value,
      rule,
      target
    };

    onAdd(new_data);
    clearForm();
    closeModal();
  };

  return (
    <div className={styles.formWrapper}>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Action">
            <MultiSelect
              options={effects}
              onChange={handleActionSelection}
              value={selected_action}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          {selected_action?.resource_class === 'attribute' ? (
            <>
              <DetailItem title="Attribute name" required>
                <SimpleInput value={attr_key} onInput={setAttrKey} />
              </DetailItem>
              <DetailItem title="Attribute value" required>
                <SimpleInput value={attr_value} onInput={setAttrValue} />
              </DetailItem>
            </>
          ) : (
            <DetailItem title="Resource">
              <MultiSelect
                options={resources}
                onChange={handleResourceSelection}
                value={selected_resources}
                isMulti
              />
            </DetailItem>
          )}
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <Checkbox
            label="Enable delay"
            checked={show_delay_config}
            onClick={() => setShowDelayConfig((show) => !show)}
          />
        </GridColumn>
      </GridRow>
      {show_delay_config && <NeuronDelayConfig data={data} onChange={setRule} />}
      <GridRow num_of_columns={3}>
        <GridColumn />
        <GridColumn span={2}>
          <Button text="Add Response" onClick={submit} disabled={!is_forn_valid()} />
        </GridColumn>
      </GridRow>
    </div>
  );
};
